import { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useTranslate } from "../../context/TranslationContext";
import { useGetFAQQuery } from "../../redux/Apis/FAQ";

const FaqSection = () => {
  const { t } = useTranslate();
  const lang = useSelector((state) => state.language.lang);
  const { data, isLoading, refetch } = useGetFAQQuery({
    params: { lang },
  });
  const [isMainOpen, setIsMainOpen] = useState(true);
  const [openCategory, setOpenCategory] = useState(null);
  const [openQuestion, setOpenQuestion] = useState({});

  const mainFaqRef = useRef(null);
  const categoryRefs = useRef({});
  const questionRefs = useRef({});

  useEffect(() => {
    if (!mainFaqRef.current) return;

    if (isMainOpen && data?.data?.length) {
      expand(mainFaqRef.current, 600);
    } else {
      collapse(mainFaqRef.current, 600);
    }
  }, [isMainOpen, data]);

  useEffect(() => {
    Object.entries(categoryRefs.current).forEach(([key, el]) => {
      if (!el) return;

      const isOpen = parseInt(key) === openCategory;

      if (isOpen) {
        expand(el, 500);
      } else {
        collapse(el, 500);
      }
    });
  }, [openCategory]);

  useEffect(() => {
    Object.entries(questionRefs.current).forEach(([key, el]) => {
      if (!el) return;

      if (openQuestion[key]) {
        expand(el, 400);
      } else {
        collapse(el, 400);
      }
    });
  }, [openQuestion]);

  useEffect(() => {
    refetch({ params: { lang } });
  }, [lang, refetch]);

  useEffect(() => {
    if (openCategory === null) {
      setOpenQuestion({});
    }
  }, [openCategory]);

  useEffect(() => {
    if (!isMainOpen) {
      setOpenCategory(null);
      setOpenQuestion({});
    }
  }, [isMainOpen]);

  const expand = (el, duration = 400) => {
    el.dataset.animating = "expand";

    el.style.transition = `max-height ${duration}ms ease`;
    el.style.overflow = "hidden";
    el.style.maxHeight = el.scrollHeight + "px";

    const cleanup = () => {
      if (el.dataset.animating === "expand") {
        el.style.maxHeight = "none";
      }
      el.removeEventListener("transitionend", cleanup);
    };

    el.addEventListener("transitionend", cleanup);
  };

  const collapse = (el, duration = 400) => {
    // cancel any pending expand cleanup
    el.dataset.animating = "collapse";

    el.style.transition = `max-height ${duration}ms ease`;
    el.style.overflow = "hidden";

    // if it was 'none', convert to px first
    if (el.style.maxHeight === "none") {
      el.style.maxHeight = el.scrollHeight + "px";
    }

    void el.offsetHeight; // force reflow
    el.style.maxHeight = "0px";
  };

  return (
    <section className="faq py-sm-5 py-4">
      <div className="container">
        <div className="row">
          <div className="col-12 text-center">
            <h2
              className="gallery-title text-uppercase text-white mb-3"
              style={{ cursor: "pointer" }}
              onClick={() => setIsMainOpen(!isMainOpen)}
            >
              {t("faq.question")}
            </h2>
            <p className="mb-4 p-lg l-grey-text">{t("faq.answer")}</p>
          </div>

          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner
                animation="border"
                style={{ color: "var(--primaryColor)" }}
              />
            </div>
          ) : (
            <div
              ref={mainFaqRef}
              style={{
                overflow: "hidden",
              }}
            >
              <div className="col-xxl-7 mx-auto mt-4 col-xl-8 col-lg-9 col-md-10 col-12">
                <div className="accordion faq-accordion" id="mainFaqAccordion">
                  {data?.data?.map((category, catIdx) => (
                    <div key={category._id} className="mb-3">
                      <div
                        className="accordion-item"
                        style={{
                          boxShadow: "none",
                          backgroundColor: "transparent",
                        }}
                        onClick={() =>
                          setOpenCategory((prev) => {
                            const next = prev === catIdx ? null : catIdx;
                            if (prev !== catIdx) {
                              setOpenQuestion({});
                            }

                            return next;
                          })
                        }
                      >
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button text-capitalize ${
                              openCategory === catIdx ? "" : "collapsed"
                            }`}
                            type="button"
                            style={{ fontSize: "30px", fontWeight: 500 }}
                          >
                            {category.name}
                          </button>
                        </h2>
                      </div>

                      <div
                        ref={(el) => (categoryRefs.current[catIdx] = el)}
                        className="accordion-collapse"
                        style={{
                          maxHeight: "0px",
                          overflow: "hidden",
                          transition: "max-height 0.5s ease-in-out",
                        }}
                      >
                        <div className="accordion-body">
                          {category.questions.map((q, qIdx) => {
                            const key = `${catIdx}-${qIdx}`;
                            return (
                              <div
                                className="accordion-item mb-2"
                                key={q._id}
                                onClick={() =>
                                  setOpenQuestion((prev) => ({
                                    ...prev,
                                    [key]: !prev[key],
                                  }))
                                }
                              >
                                <h2 className="accordion-header">
                                  <button
                                    className={`accordion-button ${
                                      openQuestion[key] ? "" : "collapsed"
                                    }`}
                                    type="button"
                                  >
                                    {q.que}
                                  </button>
                                </h2>
                                <div
                                  ref={(el) => (questionRefs.current[key] = el)}
                                  className="accordion-collapse"
                                  style={{
                                    maxHeight: "0px",
                                    overflow: "hidden",
                                    transition: "max-height 0.4s ease-in-out",
                                  }}
                                >
                                  <div className="accordion-body">
                                    <p className="mb-0">{q.ans}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
